/**
 * EntityReducer - Unified state management for entity collections
 *
 * This reducer provides a consistent way to manage collections of entities
 * across the application. It's used for both initial data loading and
 * real-time updates from WebSocket events.
 *
 * Key principle: Same code path for initial load and event application.
 */

export type EntityAction<T> =
  | { type: 'SET_ALL'; entities: T[] }      // Initial load / full sync
  | { type: 'UPSERT'; entity: T }           // Create or update single entity
  | { type: 'UPSERT_MANY'; entities: T[] }  // Create or update multiple entities
  | { type: 'REMOVE'; id: number | string } // Delete by ID
  | { type: 'MERGE'; entities: T[] }        // Sync merge (server state + local pending)

/**
 * Marker interface for entities with pending local changes.
 * Used during offline-online sync to preserve local mutations.
 */
export interface PendingEntity {
  _pending?: boolean;
  _pendingAction?: 'create' | 'update' | 'delete';
}

/**
 * Generic entity reducer for managing collections.
 *
 * @param state - Current state (array of entities)
 * @param action - Action to apply
 * @param sortFn - Optional sort function to maintain order
 * @returns New state with action applied
 */
export function entityReducer<T extends { id: number | string }>(
  state: T[],
  action: EntityAction<T>,
  sortFn?: (a: T, b: T) => number
): T[] {
  switch (action.type) {
    case 'SET_ALL': {
      // Replace all entities (initial load or full sync)
      return sortFn ? [...action.entities].sort(sortFn) : action.entities;
    }

    case 'UPSERT': {
      const existingIndex = state.findIndex(e => e.id === action.entity.id);
      if (existingIndex >= 0) {
        // Update existing entity
        const updated = [...state];
        updated[existingIndex] = action.entity;
        return sortFn ? updated.sort(sortFn) : updated;
      }
      // Add new entity
      const added = [...state, action.entity];
      return sortFn ? added.sort(sortFn) : added;
    }

    case 'UPSERT_MANY': {
      const entityMap = new Map(state.map(e => [e.id, e]));
      for (const entity of action.entities) {
        entityMap.set(entity.id, entity);
      }
      const result = Array.from(entityMap.values());
      return sortFn ? result.sort(sortFn) : result;
    }

    case 'REMOVE': {
      return state.filter(e => e.id !== action.id);
    }

    case 'MERGE': {
      // Server state wins, but preserve local pending items not in server state
      const serverIds = new Set(action.entities.map(e => e.id));
      const localPending = state.filter(e => {
        const pending = e as T & PendingEntity;
        return !serverIds.has(e.id) && pending._pending;
      });

      const merged = [...action.entities, ...localPending];
      return sortFn ? merged.sort(sortFn) : merged;
    }

    default: {
      // Exhaustive check - ensures all action types are handled
      const exhaustiveCheck: never = action;
      console.error('Unhandled action type:', exhaustiveCheck);
      return state;
    }
  }
}

/**
 * Creates a typed dispatch function for a specific entity type.
 * This is useful when you want to create a context-specific dispatch.
 *
 * @param setState - React setState function for the entity array
 * @param sortFn - Optional sort function
 * @returns Dispatch function for entity actions
 */
export function createEntityDispatch<T extends { id: number | string }>(
  setState: React.Dispatch<React.SetStateAction<T[] | null>>,
  sortFn?: (a: T, b: T) => number
): (action: EntityAction<T>) => void {
  return (action: EntityAction<T>) => {
    setState(prev => {
      if (prev === null) {
        // Handle null state - only SET_ALL makes sense here
        if (action.type === 'SET_ALL') {
          return sortFn ? [...action.entities].sort(sortFn) : action.entities;
        }
        return null;
      }
      return entityReducer(prev, action, sortFn);
    });
  };
}

/**
 * Common sort functions for entities
 */
export const sortByCreatedAtDesc = <T extends { created_at: string }>(a: T, b: T): number =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

export const sortByCreatedAtAsc = <T extends { created_at: string }>(a: T, b: T): number =>
  new Date(a.created_at).getTime() - new Date(b.created_at).getTime();

export const sortByName = <T extends { first_name?: string; last_name?: string; name?: string }>(
  a: T,
  b: T
): number => {
  const nameA = a.name ?? `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim();
  const nameB = b.name ?? `${b.first_name ?? ''} ${b.last_name ?? ''}`.trim();
  return nameA.localeCompare(nameB);
};

/**
 * Creates a sort function that combines multiple sort criteria
 */
export function createMultiSort<T>(...sortFns: ((a: T, b: T) => number)[]): (a: T, b: T) => number {
  return (a: T, b: T) => {
    for (const sortFn of sortFns) {
      const result = sortFn(a, b);
      if (result !== 0) return result;
    }
    return 0;
  };
}
